import { decodeHtmlEntities } from 'lib/htmlEntities'

// Platforms like X, Facebook and Slack render emoji as <img> tags pointing at
// their own emoji assets, so copying text from them puts images on the
// clipboard where the reader sees characters. ImageExtension parses every
// pasted <img>, so without this transform a wave in the middle of a sentence
// becomes a block image node with no intrinsic size, rendering full width.

// One emoji grapheme cluster: keycaps, flag pairs, or a pictographic base with
// optional presentation selector / skin tone / tag sequence, joined by ZWJ.
const EMOJI_TEXT_PATTERN =
    /^(?:[0-9#*]️?⃣|\p{RI}{2}|\p{Extended_Pictographic}️?(?:\p{Emoji_Modifier})?(?:[\u{E0020}-\u{E007E}]+\u{E007F})?)(?:‍(?:\p{Extended_Pictographic}️?(?:\p{Emoji_Modifier})?|\p{RI}{2}))*$/u

const TWEMOJI_ASSET_SRC_PATTERN = /(?:^|[./_-])twemoji(?:[./_-]|$)/i

const EMOJI_CODEPOINT_ASSET_SRC_PATTERN = /\/emoji(?:s|-datasource)?\/|\/images\/emoji\.php\//i

// An emoji alt is useful only when the source also identifies the image as an
// emoji asset. Otherwise a real content image with alt="👋" would be discarded.
const KNOWN_EMOJI_ASSET_SRC_PATTERN =
    /abs-0\.twimg\.com\/emoji\/|fbcdn\.net\/images\/emoji\.php\/|emoji\.slack-edge\.com\/|cdn\.discordapp\.com\/emojis\//i

const CODEPOINT_FILE_PATTERN = /^[0-9a-f]{2,6}(?:-[0-9a-f]{2,6})*$/i

const IMAGE_EXTENSION_PATTERN = /\.(?:svg|png|gif|webp|jpe?g)$/i

// Match a complete img tag without treating `>` inside a quoted attribute as
// the end of the tag.
const IMG_TAG_PATTERN = /<img\b(?:[^"'<>]|"[^"]*"|'[^']*')*>/gi

const MAX_CODE_POINT = 0x10ffff

const fromCodePoint = (value: number): string =>
    Number.isFinite(value) && value >= 0 && value <= MAX_CODE_POINT
        ? String.fromCodePoint(value)
        : ''

const readAttribute = (tag: string, name: string): string | null => {
    const match = tag.match(
        new RegExp(`(?:^|\\s)${name}\\s*=\\s*(?:"([^"]*)"|'([^']*)'|([^\\s"'>]+))`, 'i'),
    )
    if (!match) return null

    return decodeHtmlEntities(match[1] ?? match[2] ?? match[3] ?? '')
}

// Emoji asset filenames are the codepoint sequence they depict
// (`.../1f1fa-1f1f8.svg`), so the character survives even when alt is missing.
const emojiTextFromSrc = (src: string): string | null => {
    if (!TWEMOJI_ASSET_SRC_PATTERN.test(src) && !EMOJI_CODEPOINT_ASSET_SRC_PATTERN.test(src)) {
        return null
    }

    const fileName = src.split(/[?#]/)[0].split('/').pop() || ''
    const codePoints = fileName.replace(IMAGE_EXTENSION_PATTERN, '')
    if (!CODEPOINT_FILE_PATTERN.test(codePoints)) return null

    const text = codePoints
        .split('-')
        .map(part => fromCodePoint(parseInt(part, 16)))
        .join('')

    return EMOJI_TEXT_PATTERN.test(text) ? text : null
}

export const emojiTextForImageAttributes = ({
    alt,
    src,
}: {
    alt?: string | null
    src?: string | null
}): string | null => {
    const srcEmoji = src ? emojiTextFromSrc(src) : null
    if (
        alt &&
        EMOJI_TEXT_PATTERN.test(alt) &&
        (srcEmoji ||
            (src &&
                (TWEMOJI_ASSET_SRC_PATTERN.test(src) || KNOWN_EMOJI_ASSET_SRC_PATTERN.test(src))))
    ) {
        return alt
    }

    return srcEmoji
}

export const emojiTextForPastedImage = (tag: string): string | null => {
    const src = readAttribute(tag, 'src')
    const alt = readAttribute(tag, 'alt')

    return emojiTextForImageAttributes({ alt, src })
}

export const replaceEmojiImagesWithText = (html: string): string =>
    html.replace(IMG_TAG_PATTERN, tag => emojiTextForPastedImage(tag) ?? tag)
